import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import LoginPage from '../presentation/components/Login/LoginPage';
import RegisterPage from '../presentation/components/Register/RegisterPage';
import RegisterStep1 from '../presentation/components/Register/RegisterStep1';
import RegisterStep2 from '../presentation/components/Register/RegisterStep2';
import RegisterStep3 from '../presentation/components/Register/RegisterStep3';
import DashboardPage from '../presentation/components/Dashboard/DashboardPage';
import ExamManagerPage from '../presentation/components/ExamManager/ExamManagerPage';
import ExamSummaryPage from '../presentation/components/ExamManager/ExamSummaryPage';
import ExamPaperPage from '../presentation/components/ExamManager/ExamPaperPage';
import AnswerBreakdownPage from '../presentation/components/ExamManager/AnswerBreakdownPage';
import YourResultPage from '../presentation/components/ExamManager/YourResultPage';
import ForgotPasswordPage from '../presentation/components/ForgotPassword/ForgotPasswordPage';
import ResetEmail from '../presentation/components/ForgotPassword/ResetEmail';
import ResetMobile from '../presentation/components/ForgotPassword/ResetMobile';
import OtpVerify from '../presentation/components/ForgotPassword/OtpVerify';
import SetPassword from '../presentation/components/ForgotPassword/SetPassword';
import SettingsPage from '../presentation/components/Settings/SettingsPage';
import SettingsNotificationsPage from '../presentation/components/Settings/SettingsNotificationsPage';
import { AppLayout, PageBanner, CustomBreadcrumbs } from '../presentation/common';

import { getCookie } from '../infrastructure/utils/cookieStorage';

// Handler for desktop QR enroll redirects
const EnrollRedirectHandler = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  if (code) {
    localStorage.setItem('pending_enroll_code', code);
  }
  const token = getCookie('authToken') || sessionStorage.getItem('authToken');
  return token ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />;
};

// Route Guard to protect routes after login
const ProtectedRoute = ({ children }) => {
  const token = getCookie('authToken') || sessionStorage.getItem('authToken');
  return token ? <AppLayout>{children}</AppLayout> : <Navigate to="/" replace />;
};

// Generic placeholder page for un-implemented subpages to keep layout running
const PlaceholderPage = ({ title }) => {
  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: title }
  ];

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />
      <PageBanner
        title={title}
        description={`Manage, edit, and organize all details for ${title} seamlessly under the unified Clean Architecture dashboard.`}
      />
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
        color: '#64748b',
        marginTop: '20px'
      }}>
        All sidebar menus and layout components are successfully running under the Clean Architecture framework.
      </div>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/enroll" element={<EnrollRedirectHandler />} />
      {/* Nested Register Step Routes */}
      <Route path="/register" element={<RegisterPage />}>
        <Route index element={<Navigate to="step-1" replace />} />
        <Route path="step-1" element={<RegisterStep1 />} />
        <Route path="step-2" element={<RegisterStep2 />} />
        <Route path="step-3" element={<RegisterStep3 />} />
      </Route>

      {/* Nested Forgot Password Step Routes */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />}>
        <Route index element={<Navigate to="email" replace />} />
        <Route path="email" element={<ResetEmail />} />
        <Route path="mobile" element={<ResetMobile />} />
        <Route path="otp" element={<OtpVerify />} />
        <Route path="new-password" element={<SetPassword />} />
      </Route>

      {/* Protected Routes (Automatically wrapped in AppLayout via ProtectedRoute) */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/exam-manager" element={<ProtectedRoute><ExamManagerPage /></ProtectedRoute>} />
      <Route path="/exam-manager/exams/:teacherId" element={<ProtectedRoute><ExamManagerPage /></ProtectedRoute>} />
      <Route path="/exam-manager/exams/:teacherId/:activeTab" element={<ProtectedRoute><ExamManagerPage /></ProtectedRoute>} />
      <Route path="/exam-manager/exam-summary/:classExamId" element={<ProtectedRoute><ExamSummaryPage /></ProtectedRoute>} />
      <Route path="/exam-manager/take-exam/:classExamId" element={<ProtectedRoute><ExamPaperPage /></ProtectedRoute>} />
      <Route path="/exam-manager/answer-breakdown/:classExamId" element={<ProtectedRoute><AnswerBreakdownPage /></ProtectedRoute>} />
      <Route path="/your-result" element={<ProtectedRoute><YourResultPage /></ProtectedRoute>} />
      <Route path="/your-result/:teacherId" element={<ProtectedRoute><YourResultPage /></ProtectedRoute>} />
      <Route path="/student-manager" element={<ProtectedRoute><PlaceholderPage title="Student Manager" /></ProtectedRoute>} />
      <Route path="/question-bank-private" element={<ProtectedRoute><PlaceholderPage title="Question Bank" /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotificationsPage /></ProtectedRoute>} />

      {/* Fallback Redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
